import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Work as WorkIcon,
  LocationOn as LocationIcon,
  AttachMoney as SalaryIcon,
  People as PeopleIcon,
  Upload as UploadIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import apiService from '../services/api';

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadJobDetails = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get all jobs and find the specific one
      const jobs = await apiService.getJobs();
      const foundJob = jobs.find(j => j.jobId === jobId);
      
      if (!foundJob) {
        setError('Job not found');
        return;
      }
      
      setJob(foundJob);
    } catch (err) {
      setError('Failed to load job details');
      console.error('Job detail loading error:', err);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadJobDetails();
  }, [loadJobDetails]);

  const handleBack = () => {
    navigate('/jobs');
  };

  const handleManageResumes = () => {
    navigate(`/resume-management?jobId=${jobId}`);
  };

  const handleCandidateMatching = () => {
    navigate(`/candidate-matching?jobId=${jobId}`);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Jobs
        </Button>
      </Container>
    );
  }

  if (!job) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Job not found
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Jobs
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          Back to Jobs
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Job Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Job Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <WorkIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h5" component="h2">
                  {job.title}
                </Typography>
                <Chip 
                  label={job.status || 'active'} 
                  color={job.status === 'active' ? 'success' : 'default'}
                  sx={{ ml: 2 }}
                />
              </Box>

              <Box display="flex" flexWrap="wrap" gap={2} mb={3}>
                <Box display="flex" alignItems="center">
                  <LocationIcon color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {job.location || 'Not specified'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <SalaryIcon color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {job.salary || 'Salary not specified'}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center">
                  <PeopleIcon color="action" sx={{ mr: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    {job.department || 'Department not specified'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                Job Description
              </Typography>
              <Typography variant="body1" paragraph>
                {job.description || 'No description available'}
              </Typography>

              {job.skills && job.skills.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Required Skills
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                    {job.skills.map((skill, index) => (
                      <Chip key={index} label={skill} variant="outlined" />
                    ))}
                  </Box>
                </>
              )}

              {job.requirements && job.requirements.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Requirements
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    {job.requirements.map((req, index) => (
                      <Typography key={index} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {req}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Actions Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Job Actions
              </Typography>
              
              <Box display="flex" flexDirection="column" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={handleManageResumes}
                  fullWidth
                >
                  Manage Resumes
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<AnalyticsIcon />}
                  onClick={handleCandidateMatching}
                  fullWidth
                >
                  AI Candidate Matching
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Job Information
              </Typography>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Job ID:</strong> {job.jobId}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Posted:</strong> {job.posted || job.created_at?.split('T')[0] || 'Unknown'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Type:</strong> {job.type || 'Full-time'}
                </Typography>
                <Typography variant="body2">
                  <strong>Candidates:</strong> {job.candidates?.length || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default JobDetailPage;
